import React from 'react'

import __debounce from 'lodash/debounce'
import { useFormContext, useWatch } from 'react-hook-form'
import styled from 'styled-components'

import { saveDraft } from '@services/messageToAccountant/draft'
import type { MessageToAccountantTopic } from '@services/messageToAccountant/types'

import {
  CopyAndPasteIconButton,
  CustomDialogActions,
  InformationalMessage,
  ReactHookForm,
  ReactHookFormTextareaField,
  SlideOverBody,
} from '@components/ui'

import type { MessageToAccountantFormValues } from './types'

const DRAFT_SAVE_DEBOUNCE_TIME = 400

export const MESSAGE_FIELD_MIN_ROWS = 6

// Fills the SlideOver panel so the body scrolls and the actions stay below it.
export const StyledReactHookForm = styled(ReactHookForm)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
` as typeof ReactHookForm

export const StyledSlideOverBody = styled(SlideOverBody)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const MessageFieldWrapperDiv = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`

export const CopyButtonPositionerDiv = styled.div`
  position: absolute;
  top: 30px;
  right: 2px;
  z-index: 1;
`

export const StyledInformationalMessage = styled(InformationalMessage)`
  margin-bottom: 10px;
`

// Both actions sit centered as one group, rather than pushed to the opposite edges.
export const ActionsDiv = styled(CustomDialogActions)`
  gap: 20px;
`

// Takes the body's leftover height, and gives it back to the helper text when validation fails.
export const StyledTextareaField = styled(ReactHookFormTextareaField)`
  flex: 1;
  min-height: 0;

  /* The element wrapping the textarea, which has no class of its own to target. */
  > div {
    flex: 1;
    align-items: stretch;
    /* Below this the SlideOver body scrolls instead of the field collapsing. */
    min-height: calc(${MESSAGE_FIELD_MIN_ROWS} * 1.3rem + 2 * var(--input-vertical-padding) + 2px);
  }

  textarea {
    /* min/max-height outrank the inline height the auto-resizing sets, so the flex height wins. */
    min-height: 100%;
    max-height: 100%;
    /* Adjusted due to the copy button */
    padding-right: 38px;
  }
`

export function MessageCopyButton() {
  const message = useWatch<MessageToAccountantFormValues, 'message'>({ name: 'message' })

  return <CopyAndPasteIconButton text={message ?? ''} />
}

interface DraftPersisterProps {
  hasSentRef: React.RefObject<boolean>
  userId: User['id']
}

/**
 * Backs the form values up to sessionStorage while the user types. Subscribes to the form instead of
 * using `useWatch` so keystrokes don't re-render the fields.
 */
export function DraftPersister({ hasSentRef, userId }: DraftPersisterProps) {
  const { watch } = useFormContext<MessageToAccountantFormValues>()

  React.useEffect(() => {
    const debouncedSave = __debounce((values: Partial<MessageToAccountantFormValues>) => {
      // A save queued right before a successful send must not resurrect the cleared draft.
      if (!hasSentRef.current) {
        saveDraft(userId, values)
      }
    }, DRAFT_SAVE_DEBOUNCE_TIME)

    const subscription = watch(values => debouncedSave(values))

    return () => {
      subscription.unsubscribe()
      // Closing the SlideOver unmounts the form, so a pending save has to land now or never.
      debouncedSave.flush()
    }
  }, [hasSentRef, userId, watch])

  return null
}

interface StaleTopicResetProps {
  topics: MessageToAccountantTopic[]
  topicsFetched: boolean | undefined
}

/** Drops a restored topic the backend no longer offers. */
export function StaleTopicReset({ topics, topicsFetched }: StaleTopicResetProps) {
  const { getValues, setValue } = useFormContext<MessageToAccountantFormValues>()

  React.useEffect(() => {
    if (!topicsFetched) {
      return
    }

    const topic = getValues('topic')
    if (topic !== null && !topics.some(({ id }) => id === topic)) {
      setValue('topic', null)
    }
  }, [getValues, setValue, topics, topicsFetched])

  return null
}
